// Utils: metaPixel
// Purpose: Client-side Meta Pixel helpers with strict value/currency formatting
// and event ID deduplication aligned with server-side tracking.

import { SERVER_URL } from '../../api/core/index';

// Your Meta Pixel ID
const META_PIXEL_ID = '1069948151905066';

/**
 * Generates a unique event ID for deduplication that matches server-side format
 * Format: purchase_sessionId_timestamp (consistent with server)
 */
const generateEventId = (eventType, sessionId = null) => {
  const timestamp = Date.now();
  const session = sessionId || sessionStorage.getItem('user_session_id') || 'no_session';
  
  // FIXED: Use same format as server for proper deduplication
  return `${eventType}_${session}_${timestamp}`.substring(0, 50); // Meta has 50 char limit
};

/**
 * Standardized value formatting for Meta Pixel
 * CRITICAL: Must match server-side exactly for deduplication
 */
const formatMetaValue = (amountInCents) => {
  if (!amountInCents || amountInCents <= 0) {
    console.warn('⚠️ Invalid amount for Meta Pixel:', amountInCents);
    return null;
  }
  
  // FIXED: Always return exact 2 decimal places to match server
  const dollars = amountInCents / 100;
  return parseFloat(dollars.toFixed(2));
};

/**
 * Standardized currency formatting for Meta Pixel
 * CRITICAL: Must be uppercase USD to match server-side exactly
 */
const formatMetaCurrency = (currency = 'USD') => {
  // FIXED: Always uppercase to ensure exact match with server
  return currency.toUpperCase();
};

/**
 * Generate consistent content IDs for products
 */
const generateContentIds = (planName, planInterval) => {
  const normalizedPlan = (planName || 'premium').toLowerCase();
  const normalizedInterval = (planInterval || 'month').toLowerCase();
  
  // FIXED: More specific and consistent IDs
  return [
    `advocacy_lab_${normalizedPlan}_${normalizedInterval}`,
    `subscription_${normalizedPlan}`,
    'advocacy_lab_subscription'
  ];
};

/**
 * Safely calls Meta pixel function with error handling
 */
const safeFbq = (command, event, parameters = {}, options = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      // 🔍 EXPLICIT LOGGING: Show full parameters for verification
      if (event === 'Purchase') {
        console.log('🧾 Meta Pixel Purchase Event - Full Parameters:', {
          command,
          event,
          parameters,
          options,
          formattedValue: parameters.value,
          formattedCurrency: parameters.currency
        });
      }
      
      if (options && Object.keys(options).length > 0) {
        window.fbq(command, event, parameters, options);
      } else {
        window.fbq(command, event, parameters);
      }
      console.log('✅ Meta Pixel event tracked:', { command, event, parameters, options });
      return true;
    } catch (error) {
      console.error('❌ Meta Pixel tracking error:', error);
      return false;
    }
  } else {
    console.warn('⚠️ Meta Pixel not loaded, event not tracked:', { command, event, parameters });
    return false;
  }
};

/**
 * Tracks Purchase event with proper deduplication
 * Should be called after successful payment verification
 * FIXED: Ensures proper value/currency formatting and validation
 */
export const trackPurchaseEvent = ({ 
  amount, 
  currency = 'USD', 
  planName = 'Premium', 
  planInterval = 'month',
  sessionId = null 
}) => {
  // CRITICAL VALIDATION: Ensure amount is provided and valid
  if (!amount || amount <= 0) {
    console.error('❌ Meta Pixel: Invalid amount provided for Purchase event:', amount);
    return false;
  }
  
  // Generate unique event ID for deduplication (matches server format)
  const eventId = generateEventId('purchase', sessionId);
      
  // Store the event ID to prevent duplicates on page refresh
  const storageKey = `meta_purchase_tracked_${sessionId || 'unknown'}`;
  
  // Check if we already tracked this purchase
  if (sessionStorage.getItem(storageKey)) {
    console.log('🚫 Purchase already tracked, skipping duplicate');
    return false;
  }
  
  // FIXED: Standardized value and currency formatting
  const formattedValue = formatMetaValue(amount);
  const formattedCurrency = formatMetaCurrency(currency);
  
  if (formattedValue === null) {
    console.error('❌ Meta Pixel: Could not format amount for Purchase event:', amount);
    return false;
  }
  
  // FIXED: Comprehensive Purchase data with all required fields
  const purchaseData = {
    value: formattedValue, // FIXED: Standardized decimal formatting
    currency: formattedCurrency, // FIXED: Standardized uppercase currency
    content_name: `${planName} ${planInterval}ly Plan`,
    content_category: 'Subscription',
    content_type: 'product',
    content_ids: generateContentIds(planName, planInterval), // FIXED: Consistent content IDs
    num_items: 1
  };
  
  // 🔍 DEBUG: Log exactly what we're sending to Meta
  console.log('🎯 Meta Pixel Purchase Data:', {
    eventId,
    purchaseData,
    sessionId,
    amount: `${amount} cents = $${formattedValue}`,
    originalCurrency: currency,
    formattedCurrency
  });
  
  // Track the purchase event with event ID for deduplication
  const success = safeFbq('track', 'Purchase', purchaseData, { eventID: eventId });
  
  if (success) {
    // Mark this purchase as tracked
    sessionStorage.setItem(storageKey, eventId);
    
    // Also store in a general list for debugging
    const trackedPurchases = JSON.parse(localStorage.getItem('meta_tracked_purchases') || '[]');
    trackedPurchases.push({
      eventId,
      sessionId,
      amount,
      formattedValue,
      formattedCurrency,
      planName,
      planInterval,
      timestamp: Date.now(),
      contentIds: purchaseData.content_ids
    });
    localStorage.setItem('meta_tracked_purchases', JSON.stringify(trackedPurchases));
    
    console.log('💰 Purchase event tracked successfully:', { 
      eventId, 
      amount: `${amount} cents`, 
      formattedValue: `$${formattedValue}`,
      currency: formattedCurrency,
      planName, 
      planInterval 
    });
    return eventId;
  }
  
  return false;
};

/**
 * Tracks InitiateCheckout event when user starts checkout process
 * FIXED: Added proper value validation and formatting
 */
export const trackInitiateCheckout = ({ 
  content_name, 
  content_category = 'Subscription', 
  value, 
  content_ids = [], 
  content_type = 'product',
  planName = 'Premium',
  planInterval = 'month'
}) => {
  const eventId = generateEventId('initiate_checkout');
  
  // FIXED: Validate and format value if provided
  let formattedValue = null;
  if (value && value > 0) {
    // If value is already in dollars, use it directly; if in cents, convert
    formattedValue = value > 100 ? formatMetaValue(value) : parseFloat(value.toFixed(2));
  }
      
  const checkoutData = {
    content_name,
    content_category,
    content_type,
    currency: formatMetaCurrency('USD'), // FIXED: Always include standardized currency
    content_ids: content_ids.length > 0 ? content_ids : generateContentIds(planName, planInterval), // FIXED: Ensure content_ids always present
    num_items: 1
  };
  
  // FIXED: Only include value if it's valid
  if (formattedValue !== null) {
    checkoutData.value = formattedValue;
  }
  
  const success = safeFbq('track', 'InitiateCheckout', checkoutData, { eventID: eventId });
  
  if (success) {
    console.log('🛒 InitiateCheckout event tracked:', { 
      eventId, 
      content_name, 
      value: formattedValue ? `$${formattedValue}` : 'no value',
      content_ids: checkoutData.content_ids
    });
    return eventId;
  }
  
  return false;
};

/**
 * Tracks ViewContent event for pricing page views
 */
export const trackViewContent = ({ content_name, content_category = 'Pricing', value = null }) => {
  const eventId = generateEventId('view_content');
  
  const contentData = {
    content_name,
    content_category,
    content_type: 'product'
  };
  
  if (value && value > 0) {
    // FIXED: Apply same value formatting
    const formattedValue = value > 100 ? formatMetaValue(value) : parseFloat(value.toFixed(2));
    if (formattedValue !== null) {
      contentData.value = formattedValue;
      contentData.currency = formatMetaCurrency('USD');
    }
  }
  
  return safeFbq('track', 'ViewContent', contentData, { eventID: eventId });
};

/**
 * Tracks Lead event for signups
 */
export const trackLead = ({ content_name = 'Signup', value = null }) => {
      const eventId = generateEventId('lead');
      
  const leadData = {
    content_name,
    content_category: 'Registration'
  };
  
  if (value && value > 0) {
    // FIXED: Apply same value formatting
    const formattedValue = value > 100 ? formatMetaValue(value) : parseFloat(value.toFixed(2));
    if (formattedValue !== null) {
      leadData.value = formattedValue;
      leadData.currency = formatMetaCurrency('USD');
    }
  }
  
  return safeFbq('track', 'Lead', leadData, { eventID: eventId });
};

/**
 * Tracks custom events
 */
export const trackCustomEvent = (eventName, parameters = {}) => {
  const eventId = generateEventId('custom');
  return safeFbq('trackCustom', eventName, parameters, { eventID: eventId });
};

/**
 * Server-side tracking helper
 * Sends events to your server to forward to Meta's Conversions API
 */
export const sendServerSideEvent = async (eventType, eventData, eventId) => {
  try {
    // Use centralized server URL
    const endpoint = `${SERVER_URL}/meta-pixel/track`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getCurrentUserToken()}`
      },
      body: JSON.stringify({
        eventType,
        eventData,
        eventId,
        timestamp: Date.now(),
        sourceUrl: window.location.href,
        userAgent: navigator.userAgent
      })
    });
    
    if (!response.ok) {
      throw new Error(`Server-side tracking failed: ${response.status}`);
    }
    
    console.log('✅ Server-side event sent:', eventType);
    return true;
  } catch (error) {
    console.error('❌ Server-side tracking error:', error);
    return false;
  }
};

/**
 * Helper to get current user token
 */
const getCurrentUserToken = async () => {
  try {
    const { auth } = await import('../../firebase/firebase');
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
  } catch (error) {
    console.error('Error getting user token:', error);
  }
  return null;
};

/**
 * Comprehensive purchase tracking that handles both client and server-side
 * FIXED: Uses standardized formatting and validation
 */
export const trackPurchaseComplete = async ({ 
  amount, 
  currency = 'USD', 
  planName = 'Premium', 
  planInterval = 'month',
  sessionId = null,
  userId = null 
}) => {
  // CRITICAL VALIDATION: Ensure amount is provided and valid
  if (!amount || amount <= 0) {
    console.error('❌ Meta Pixel: Invalid amount provided for Complete Purchase tracking:', amount);
    return false;
  }
  
  // Generate event ID for deduplication across client and server
  const eventId = generateEventId('purchase', sessionId);
  
  // Check for duplicates
  const storageKey = `meta_purchase_tracked_${sessionId || 'unknown'}`;
  if (sessionStorage.getItem(storageKey)) {
    console.log('🚫 Purchase already tracked, skipping duplicate');
    return false;
  }
  
  // FIXED: Standardized value and currency formatting
  const formattedValue = formatMetaValue(amount);
  const formattedCurrency = formatMetaCurrency(currency);
  
  if (formattedValue === null) {
    console.error('❌ Meta Pixel: Could not format amount for Complete Purchase tracking:', amount);
    return false;
  }
  
  const purchaseData = {
    value: formattedValue, // FIXED: Standardized formatting
    currency: formattedCurrency, // FIXED: Standardized currency
    content_name: `${planName} ${planInterval}ly Plan`,
    content_category: 'Subscription',
    content_type: 'product',
    content_ids: generateContentIds(planName, planInterval), // FIXED: Consistent content IDs
    num_items: 1
  };
  
  // Track client-side
  const clientSuccess = safeFbq('track', 'Purchase', purchaseData, { eventID: eventId });
  
  // Track server-side for redundancy and improved attribution
  const serverSuccess = await sendServerSideEvent('Purchase', {
    ...purchaseData,
    userId,
    sessionId
  }, eventId);
  
  if (clientSuccess) {
    // Mark as tracked to prevent duplicates
    sessionStorage.setItem(storageKey, eventId);
    
    // Store for debugging
    const trackedPurchases = JSON.parse(localStorage.getItem('meta_tracked_purchases') || '[]');
    trackedPurchases.push({
      eventId,
      sessionId,
      userId,
      amount,
      formattedValue,
      formattedCurrency,
      planName,
      planInterval,
      timestamp: Date.now(),
      clientTracked: clientSuccess,
      serverTracked: serverSuccess,
      contentIds: purchaseData.content_ids
    });
    localStorage.setItem('meta_tracked_purchases', JSON.stringify(trackedPurchases));
    
    console.log('💰 Complete purchase tracking done:', { 
      eventId, 
      clientSuccess, 
      serverSuccess,
      amount: `${amount} cents`,
      formattedValue: `$${formattedValue}`,
      currency: formattedCurrency
    });
  }
  
  return eventId;
};

/**
 * Debug function to view tracked purchases
 */
export const debugMetaPixel = () => {
  console.log('🔍 Meta Pixel Debug Info:');
  console.log('Pixel loaded:', typeof window !== 'undefined' && !!window.fbq);
  console.log('Tracked purchases:', JSON.parse(localStorage.getItem('meta_tracked_purchases') || '[]'));
  console.log('Session storage keys:', Object.keys(sessionStorage).filter(key => key.includes('meta_')));
  
  // FIXED: Added value/currency validation debugging
  const purchases = JSON.parse(localStorage.getItem('meta_tracked_purchases') || '[]');
  if (purchases.length > 0) {
    console.log('Value/Currency Analysis:');
    purchases.forEach((purchase, index) => {
      console.log(`Purchase ${index + 1}:`, {
        originalAmount: purchase.amount,
        formattedValue: purchase.formattedValue,
        currency: purchase.formattedCurrency,
        eventId: purchase.eventId,
        contentIds: purchase.contentIds
      });
    });
  }
};

// Make debug function globally available
if (typeof window !== 'undefined') {
  window.debugMetaPixel = debugMetaPixel;
}

export default {
  trackPurchaseEvent,
  trackInitiateCheckout,
  trackViewContent,
  trackLead,
  trackCustomEvent,
  trackPurchaseComplete,
  debugMetaPixel
}; 